import { Box, Chip, Typography } from "@mui/material";
import { CompanyNumber } from "..";
import { FlowMobilePlanConfig } from "../../..";
import { AssignedNumberBlock } from "./AssignedNumberBlock";
import { PendingNumberBlock } from "./PendingNumberBlock";
import { PlanBenefits } from "./PlanBenefits";
import { PlanMonthlyInvoicesList } from "./PlanMonthlyInvoicesList";

type PlanRowProps = {
  plan: FlowMobilePlanConfig;
  plans: FlowMobilePlanConfig[];
  companyNumbersByUid: Map<string, CompanyNumber>;
  availableNumbers: CompanyNumber[];
};

export function PlanRow({ plan, plans, companyNumbersByUid, availableNumbers }: PlanRowProps) {
  const companyNumber = plan.externalId ? (companyNumbersByUid.get(plan.externalId) ?? null) : null;
  const assignedPhoneNumber = companyNumber?.phoneNumber ?? null;

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: "var(--fx-surface-text, #0F172A)",
                lineHeight: 1.2,
              }}
            >
              {plan.name}
            </Typography>

            {plan.status && (
              <Chip
                label={`PLAN: ${plan.status.toUpperCase()}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor:
                    plan.status === "active" || plan.status === "completed"
                      ? "rgba(76, 175, 80, 0.08)"
                      : "rgba(244, 67, 54, 0.08)",
                  color:
                    plan.status === "active" || plan.status === "completed" ? "#2e7d32" : "#d32f2f",
                  border:
                    plan.status === "active" || plan.status === "completed"
                      ? "1px solid rgba(76, 175, 80, 0.16)"
                      : "1px solid rgba(244, 67, 54, 0.16)",
                }}
              />
            )}
          </Box>

          <PlanBenefits features={plan.features} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", sm: "flex-end" },
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {plan.expDate && (
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--fx-surface-textsecondary, #64748B)" }}>
              Expires:{" "}
              {new Date(plan.expDate).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
          )}

          {assignedPhoneNumber && companyNumber ? (
            <AssignedNumberBlock
              companyNumberId={companyNumber.uid}
              phone={assignedPhoneNumber}
              name={companyNumber.name}
              status={companyNumber.status}
              source={companyNumber.source}
            />
          ) : null}
        </Box>
      </Box>

      {assignedPhoneNumber && companyNumber ? (
        companyNumber.status === "ACTIVE" ? (
          <PlanMonthlyInvoicesList
            phoneNumber={assignedPhoneNumber}
            planSubscriptionId={plan.subscriptionId || plan.id}
          />
        ) : null
      ) : (
        <>
          <Box sx={{ borderBottom: "1px dashed rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)" }} />
          <PendingNumberBlock plan={plan} plans={plans} availableNumbers={availableNumbers} />
        </>
      )}
    </Box>
  );
}
